/**
 * Utility functions for parsing and replacing variables in canned responses
 * Supports named variables {{variable_name}} in all fields including:
 * - Message content
 * - Interactive message options
 * - Card titles, descriptions
 * - Button text
 */

/**
 * Extract all unique variable names from a text string
 * @param {string} text - Text containing {{variable_name}} patterns
 * @returns {Array<string>} Array of unique variable names (without braces)
 */
export function extractVariablesFromText(text) {
  if (!text) return [];

  const regex = /\{\{([a-zA-Z][a-zA-Z0-9_]*)\}\}/g;
  const variables = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    const varName = match[1];
    if (!variables.includes(varName)) {
      variables.push(varName);
    }
  }

  return variables;
}

/**
 * Extract all variables from a canned response payload
 * Recursively searches through content and content_attributes
 * @param {Object} cannedResponse - Canned response object with content_type and content_attributes
 * @returns {Array<string>} Array of unique variable names found anywhere in the payload
 */
export function extractVariablesFromCannedResponse(cannedResponse) {
  const allVariables = new Set();

  // Extract from content field
  if (cannedResponse.content) {
    extractVariablesFromText(cannedResponse.content).forEach(v =>
      allVariables.add(v)
    );
  }

  // Extract from content_attributes based on content_type
  const contentAttrs = cannedResponse.content_attributes;
  if (!contentAttrs) return Array.from(allVariables);

  const contentType = cannedResponse.content_type;

  if (contentType === 'input_select') {
    // Options type - check options array
    if (contentAttrs.items) {
      contentAttrs.items.forEach(option => {
        if (option.title) {
          extractVariablesFromText(option.title).forEach(v =>
            allVariables.add(v)
          );
        }
        if (option.value) {
          extractVariablesFromText(option.value).forEach(v =>
            allVariables.add(v)
          );
        }
        if (option.description) {
          extractVariablesFromText(option.description).forEach(v =>
            allVariables.add(v)
          );
        }
      });
    }
  } else if (contentType === 'cards') {
    // Cards type - check cards array
    if (contentAttrs.items) {
      contentAttrs.items.forEach(card => {
        if (card.title) {
          extractVariablesFromText(card.title).forEach(v =>
            allVariables.add(v)
          );
        }
        if (card.description) {
          extractVariablesFromText(card.description).forEach(v =>
            allVariables.add(v)
          );
        }
        if (card.media_url) {
          extractVariablesFromText(card.media_url).forEach(v =>
            allVariables.add(v)
          );
        }
        // Check card actions
        if (card.actions) {
          card.actions.forEach(action => {
            if (action.text) {
              extractVariablesFromText(action.text).forEach(v =>
                allVariables.add(v)
              );
            }
            if (action.uri) {
              extractVariablesFromText(action.uri).forEach(v =>
                allVariables.add(v)
              );
            }
            if (action.payload) {
              extractVariablesFromText(action.payload).forEach(v =>
                allVariables.add(v)
              );
            }
          });
        }
      });
    }
  } else if (contentType === 'catalog_product') {
    // WhatsApp catalog product
    if (contentAttrs.body) {
      extractVariablesFromText(contentAttrs.body).forEach(v =>
        allVariables.add(v)
      );
    }
    if (contentAttrs.footer) {
      extractVariablesFromText(contentAttrs.footer).forEach(v =>
        allVariables.add(v)
      );
    }
  } else if (contentType === 'flow') {
    // WhatsApp flow
    if (contentAttrs.header) {
      extractVariablesFromText(contentAttrs.header).forEach(v =>
        allVariables.add(v)
      );
    }
    if (contentAttrs.body) {
      extractVariablesFromText(contentAttrs.body).forEach(v =>
        allVariables.add(v)
      );
    }
    if (contentAttrs.footer) {
      extractVariablesFromText(contentAttrs.footer).forEach(v =>
        allVariables.add(v)
      );
    }
  }

  return Array.from(allVariables);
}

/**
 * Replace all variables in text with provided values
 * @param {string} text - Text containing {{variable_name}} patterns
 * @param {Object} variableValues - Object mapping variable names to their values
 * @returns {string} Text with variables replaced
 */
export function replaceVariablesInText(text, variableValues) {
  if (!text) return text;

  let result = text;
  Object.keys(variableValues).forEach(varName => {
    const value = variableValues[varName];
    const regex = new RegExp(`\\{\\{${varName}\\}\\}`, 'g');
    result = result.replace(regex, value || '');
  });

  return result;
}

/**
 * Replace all variables in a canned response payload
 * Creates a deep copy and replaces variables everywhere
 * @param {Object} cannedResponse - Canned response object
 * @param {Object} variableValues - Object mapping variable names to their values
 * @returns {Object} New canned response object with variables replaced
 */
export function replaceVariablesInCannedResponse(
  cannedResponse,
  variableValues
) {
  // Deep clone to avoid mutation
  const result = JSON.parse(JSON.stringify(cannedResponse));

  // Replace in content field
  if (result.content) {
    result.content = replaceVariablesInText(result.content, variableValues);
  }

  // Replace in content_attributes based on content_type
  const contentAttrs = result.content_attributes;
  if (!contentAttrs) return result;

  const contentType = result.content_type;

  if (contentType === 'input_select') {
    // Options type
    if (contentAttrs.items) {
      contentAttrs.items = contentAttrs.items.map(option => ({
        ...option,
        title: replaceVariablesInText(option.title, variableValues),
        value: replaceVariablesInText(option.value, variableValues),
        description: option.description
          ? replaceVariablesInText(option.description, variableValues)
          : option.description,
      }));
    }
  } else if (contentType === 'cards') {
    // Cards type
    if (contentAttrs.items) {
      contentAttrs.items = contentAttrs.items.map(card => ({
        ...card,
        title: replaceVariablesInText(card.title, variableValues),
        description: card.description
          ? replaceVariablesInText(card.description, variableValues)
          : card.description,
        media_url: card.media_url
          ? replaceVariablesInText(card.media_url, variableValues)
          : card.media_url,
        actions: card.actions
          ? card.actions.map(action => ({
              ...action,
              text: replaceVariablesInText(action.text, variableValues),
              uri: action.uri
                ? replaceVariablesInText(action.uri, variableValues)
                : action.uri,
              payload: action.payload
                ? replaceVariablesInText(action.payload, variableValues)
                : action.payload,
            }))
          : card.actions,
      }));
    }
  } else if (contentType === 'catalog_product') {
    // WhatsApp catalog product
    if (contentAttrs.body) {
      contentAttrs.body = replaceVariablesInText(
        contentAttrs.body,
        variableValues
      );
    }
    if (contentAttrs.footer) {
      contentAttrs.footer = replaceVariablesInText(
        contentAttrs.footer,
        variableValues
      );
    }
  } else if (contentType === 'flow') {
    // WhatsApp flow
    if (contentAttrs.header) {
      contentAttrs.header = replaceVariablesInText(
        contentAttrs.header,
        variableValues
      );
    }
    if (contentAttrs.body) {
      contentAttrs.body = replaceVariablesInText(
        contentAttrs.body,
        variableValues
      );
    }
    if (contentAttrs.footer) {
      contentAttrs.footer = replaceVariablesInText(
        contentAttrs.footer,
        variableValues
      );
    }
  }

  return result;
}

/**
 * Validate that all required variables have been filled
 * @param {Array<string>} variables - Array of variable names
 * @param {Object} variableValues - Object mapping variable names to their values
 * @returns {Object} { valid: boolean, missingVariables: Array<string> }
 */
export function validateVariableFill(variables, variableValues) {
  const missingVariables = variables.filter(
    varName => !variableValues[varName] || variableValues[varName].trim() === ''
  );

  return {
    valid: missingVariables.length === 0,
    missingVariables,
  };
}
